import { Badge, makeStyles, tokens } from '@fluentui/react-components';
import type { TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import { KanbanBoard, type KanbanCardRendererProps } from '@powerportalspro/react-fluent';

// The demo's tasks, a lane for each status reason from Not Started to Canceled, each card drawn by
// the page: its subject and what it is about, when it is due, and its priority. The columns are the
// view's, so the board reads them with each card.

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.KanbanBoards.KanbanBoardCardsDemo';

// Dataverse's own "All Tasks" view, which every environment has under this id.
const ALL_TASKS_VIEW_ID = '2b5f5a5d-2d23-4fe7-aa58-e77995368ae7';

const HIGH_PRIORITY = 2;

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  subject: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  regarding: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

// The raw value behind a column.
function read(record: TableRecord, column: string): unknown {
  const cell = record.properties?.[column];
  return cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
}

export function KanbanBoardCardsSample() {
  const styles = useStyles();
  const t = useT();

  const renderCard = (card: KanbanCardRendererProps) => {
    const regarding = card.displayValue('regardingobjectid');
    const due = card.displayValue('scheduledend');
    const priority = card.displayValue('prioritycode');
    return (
      <div className={styles.card}>
        <div className={styles.subject}>{card.field('subject')}</div>
        {regarding && <div className={styles.regarding}>{regarding}</div>}
        <div className={styles.footer}>
          <span>{due ? t(`${STRINGS_BASE}.due`, [due]) : t(`${STRINGS_BASE}.no-due-date`)}</span>
          {priority && (
            <Badge
              appearance="tint"
              color={Number(read(card.record, 'prioritycode')) === HIGH_PRIORITY ? 'danger' : 'informative'}
              size="small"
            >
              {priority}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <KanbanBoard
      defaultViewId={ALL_TASKS_VIEW_ID}
      laneColumn="statuscode"
      lanes={['2', '3', '4', '7', '5', '6']}
      pageSize={5}
      height="560px"
      cardRenderer={renderCard}
    />
  );
}
